/**
 * EC-15117 Shopify's standard storefront events and actions:
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-events-and-actions
 *
 * - Defines <s-view-event>, which dispatches shopify:product:view from the payload Liquid renders into it.
 * - window.TitusStandardEvents lets the theme's components dispatch shopify:cart:lines-update,
 *   shopify:cart:error and shopify:search:update next to their own on:* events.
 * - configureShopifyActions() makes Shopify.actions.updateCart and openCart use the cart drawer. Without it,
 *   Shopify reloads the page, because the lazily loaded cart drawer isn't defined when an app calls them.
 *   header-essentials.liquid calls it on DOMContentLoaded.
 */
import {
  CartErrorEvent,
  CartLinesUpdateEvent,
  SearchUpdateEvent,
  createViewEventElement
} from 'https://cdn.shopify.com/storefront/standard-events.js';

// The AJAX cart API answers without a Storefront API error code. Shopify's own example for themes on the
// AJAX API uses this one.
const AJAX_CART_ERROR_CODE = 'SERVICE_UNAVAILABLE';

if (!customElements.get('s-view-event')) {
  customElements.define('s-view-event', createViewEventElement());
}

/**
 * Keeps a rejected event promise that no listener handles out of the console. Listeners still receive the
 * rejection through their own handlers.
 * @param {object} event - Standard event carrying a promise.
 * @returns {object} The event.
 */
function withHandledRejection(event) {
  event.promise.catch(() => {});
  return event;
}

/**
 * A cart change the theme makes through the AJAX cart API, announced as shopify:cart:lines-update.
 * Settle it with resolve(), resolveWithCurrentCart() or reject(), or listeners wait forever.
 */
class CartLinesUpdate {
  /**
   * Dispatches the event. Call it right before the cart request.
   * @param {Element} target - Product form or cart items element the change comes from.
   * @param {object} payload - action ('add', 'update' or 'remove'), context ('product', 'dialog' or
   *   'cart') and lines ({ merchandiseId, quantity } to add, { id, quantity } with the line key otherwise).
   */
  constructor(target, payload) {
    this.target = target;
    this.deferred = CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(withHandledRejection(
      new CartLinesUpdateEvent({ ...payload, promise: this.deferred.promise })
    ));
  }

  /**
   * @param {object} ajaxCart - The cart as /cart.js and /cart/change.js return it.
   */
  resolve(ajaxCart) {
    this.deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) });
  }

  /**
   * Resolves with the cart from /cart.js, for requests like /cart/add.js that return only the added item.
   */
  async resolveWithCurrentCart() {
    try {
      const response = await fetch(`${window.theme.routes.cart}.js`);
      if (!response.ok) throw new Error(response.status);
      this.resolve(await response.json());
    } catch (error) {
      this.deferred.reject(error);
    }
  }

  /**
   * Dispatches shopify:cart:error and rejects the update.
   * @param {string} message - Error message shown to the customer.
   */
  reject(message) {
    this.target.dispatchEvent(new CartErrorEvent({ error: message, code: AJAX_CART_ERROR_CODE }));
    this.deferred.reject(new Error(message));
  }
}

/**
 * @param {Element} target - Product form or cart items element the change comes from.
 * @param {object} payload - See CartLinesUpdate.
 * @returns {CartLinesUpdate}
 */
function startCartLinesUpdate(target, payload) {
  return new CartLinesUpdate(target, payload);
}

/**
 * Dispatches shopify:search:update for search results with the given URL parameters. Collection pages share
 * the filters and the results element with the search page, so this returns null there.
 * @param {Element} target - The filter form, or the results element when filters are off.
 * @param {string} searchParams - Query string of the results, with q, filter.* and sort_by.
 * @returns {?{ resolve: function(Element): void, reject: function(Error): void }} Resolve it with the
 *   #filter-results element of the new results, which carries their count.
 */
function startSearchUpdate(target, searchParams) {
  if (!window.theme.data.pageType.startsWith('search')) return null;

  const params = new URLSearchParams(searchParams);
  const deferred = SearchUpdateEvent.createPromise();
  target.dispatchEvent(withHandledRejection(new SearchUpdateEvent({
    search: {
      query: params.get('q') ?? '',
      productFilters: SearchUpdateEvent.parseProductFilters(params),
      sortKey: SearchUpdateEvent.getSortKey(params)
    },
    promise: deferred.promise
  })));

  return {
    resolve: (resultsElement) => deferred.resolve({ totalCount: Number(resultsElement.dataset.resultsCount) }),
    reject: deferred.reject
  };
}

/**
 * Announces the search results the page loaded with: a submitted search ends on this page.
 */
function dispatchSearchResultsOnLoad() {
  const results = document.getElementById('filter-results');
  if (!results) return;

  const filterForm = document.getElementById('facets');
  startSearchUpdate(filterForm ?? results, window.location.search)?.resolve(results);
}

/**
 * The cart drawer element, with cart-drawer.js loaded. Null on the cart page and with the cart type 'page'.
 * @returns {Promise<?Element>}
 */
async function loadCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && !customElements.get('cart-drawer')) {
    await window.theme.loader.cartLoader();
  }
  return cartDrawer;
}

/**
 * Re-renders the cart after Shopify.actions.updateCart changed it.
 * @param {object} cart - The updated cart (Storefront API shape).
 */
async function refreshCart(cart) {
  const cartDrawer = await loadCartDrawer();
  if (cartDrawer) {
    await cartDrawer.refresh(true);
    return;
  }

  const cartPageItems = document.querySelector('cart-items');
  if (cartPageItems && cartPageItems.dataset.empty === 'false' && cart.totalQuantity > 0) {
    await cartPageItems.refresh();
    return;
  }

  // Shopify's own fallback. The cart page's empty state is rendered by Liquid only.
  window.location.reload();
}

function goToCartPage() {
  if (window.location.pathname === window.theme.routes.cart) return;
  window.location.href = window.theme.routes.cart;
}

/**
 * Handler for Shopify.actions.openCart.
 */
async function openCart() {
  const cartDrawer = await loadCartDrawer().catch(() => null);
  if (!cartDrawer) {
    goToCartPage();
    return;
  }

  // Before its first opening, the drawer still has to load the scripts of its contents, which
  // CartDrawer.renderContents does. cart-drawer-loader.js does the same for the cart icon.
  if (!cartDrawer.scriptsLoaded) await cartDrawer.refresh(true);
  cartDrawer.open();
}

/**
 * Makes Shopify.actions.updateCart and openCart, which apps and AI agents call, use the theme's cart.
 * Only the first configure call of an action takes effect, see header-essentials.liquid.
 */
function configureShopifyActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    async handler(updateCartInShopify) {
      const result = await updateCartInShopify();
      await refreshCart(result.cart).catch(() => window.location.reload());
      return result;
    }
  });

  actions.openCart.configure({ handler: openCart });
}

window.TitusStandardEvents = {
  startCartLinesUpdate,
  startSearchUpdate,
  configureShopifyActions
};

document.addEventListener('DOMContentLoaded', dispatchSearchResultsOnLoad, { once: true });
